import type { Announcement } from '@data-hub/shared';
import { createFileRoute } from '@tanstack/react-router';
import {
  MoreHorizontalIcon,
  PencilIcon,
  PlusIcon,
  SparklesIcon,
  TrashIcon,
  XIcon,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { AnnouncementFormDialog } from '@/features/announcements/AnnouncementFormDialog';
import {
  useAnnouncements,
  useDeleteAnnouncement,
  useDismissAnnouncement,
} from '@/features/announcements/use-announcements';
import { useAuth } from '@/features/auth/auth-context';
import { useMe } from '@/features/auth/use-me';
import { handleApiError } from '@/lib/handle-error';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_authed/announcements')({
  component: AnnouncementsPage,
});

function AnnouncementsPage() {
  const { user } = useAuth();
  const me = useMe();
  const announcements = useAnnouncements();
  const deleteAnnouncement = useDeleteAnnouncement();
  const dismiss = useDismissAnnouncement();

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Announcement | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Announcement | null>(null);

  const isAdmin = me.data?.role === 'admin';
  const myEmail = user?.email ?? null;
  const items = announcements.data?.items ?? [];

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 px-4 py-8 sm:px-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Announcements</h1>
          <p className="text-sm text-muted-foreground">
            Broadcast updates to the team. Important ones get pinned to the dashboard.
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => setCreateOpen(true)}>
            <PlusIcon className="size-4" />
            New announcement
          </Button>
        )}
      </div>

      {announcements.isPending ? (
        <SkeletonList />
      ) : announcements.isError ? (
        <ErrorState
          message={announcements.error.message}
          onRetry={() => void announcements.refetch()}
        />
      ) : items.length === 0 ? (
        <EmptyState isAdmin={isAdmin} onCreate={isAdmin ? () => setCreateOpen(true) : undefined} />
      ) : (
        <ul className="flex flex-col gap-3">
          {items.map((a) => (
            <li key={a.id}>
              <AnnouncementRow
                announcement={a}
                myEmail={myEmail}
                isAdmin={isAdmin}
                onEdit={() => setEditTarget(a)}
                onDelete={() => setDeleteTarget(a)}
                onDismiss={() =>
                  dismiss.mutate(a.id, {
                    onSuccess: () => toast.success('Dismissed'),
                    onError: (err) => handleApiError(err, 'Failed to dismiss'),
                  })
                }
              />
            </li>
          ))}
        </ul>
      )}

      {isAdmin && (
        <>
          <AnnouncementFormDialog open={createOpen} onOpenChange={setCreateOpen} />
          <AnnouncementFormDialog
            key={editTarget?.id ?? 'edit'}
            open={editTarget !== null}
            onOpenChange={(open) => !open && setEditTarget(null)}
            announcement={editTarget}
          />
          <AlertDialog
            open={deleteTarget !== null}
            onOpenChange={(open) => !open && setDeleteTarget(null)}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete announcement?</AlertDialogTitle>
                <AlertDialogDescription>
                  <strong>{deleteTarget?.title}</strong> will be removed for everyone. This cannot
                  be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={deleteAnnouncement.isPending}>
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  disabled={deleteAnnouncement.isPending}
                  className="bg-destructive text-white hover:bg-destructive/90"
                  onClick={(e) => {
                    e.preventDefault();
                    if (!deleteTarget) return;
                    deleteAnnouncement.mutate(deleteTarget.id, {
                      onSuccess: () => {
                        toast.success('Announcement deleted');
                        setDeleteTarget(null);
                      },
                      onError: (err) => handleApiError(err, 'Failed to delete announcement'),
                    });
                  }}
                >
                  {deleteAnnouncement.isPending ? 'Deleting…' : 'Delete'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
}

function AnnouncementRow({
  announcement: a,
  myEmail,
  isAdmin,
  onEdit,
  onDelete,
  onDismiss,
}: {
  announcement: Announcement;
  myEmail: string | null;
  isAdmin: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onDismiss: () => void;
}) {
  const expired = !!a.expiresAt && new Date(a.expiresAt).getTime() < Date.now();
  const dismissedByMe = !!myEmail && a.dismissedBy.includes(myEmail);

  return (
    <article
      className={cn(
        'rounded-lg border bg-card p-4',
        a.important && 'border-amber-500/40 bg-amber-500/5 dark:border-amber-400/30',
        !a.isActive && 'opacity-60',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <h2 className="font-semibold">{a.title}</h2>
            {a.important && (
              <Badge
                variant="outline"
                className="gap-1 border-amber-500/40 text-amber-700 dark:text-amber-300"
              >
                <SparklesIcon className="size-3" />
                Important
              </Badge>
            )}
            {!a.isActive && <Badge variant="secondary">Inactive</Badge>}
            {expired && <Badge variant="secondary">Expired</Badge>}
            {dismissedByMe && <Badge variant="secondary">Dismissed</Badge>}
          </div>
          <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{a.body}</p>
          <p className="mt-3 text-xs text-muted-foreground">
            {formatDate(a.createdAt)}
            {isAdmin && a.dismissedBy.length > 0 && (
              <>
                {' · '}
                {a.dismissedBy.length} dismissed
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-1">
          {!dismissedByMe && a.isActive && !expired && (
            <Button
              variant="ghost"
              size="icon"
              aria-label="Dismiss"
              className="size-8"
              onClick={onDismiss}
            >
              <XIcon className="size-4" />
            </Button>
          )}
          {isAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Actions" className="size-8">
                  <MoreHorizontalIcon className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onEdit}>
                  <PencilIcon className="size-4" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem variant="destructive" onClick={onDelete}>
                  <TrashIcon className="size-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </article>
  );
}

function SkeletonList() {
  return (
    <div className="flex flex-col gap-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="rounded-lg border bg-card p-4">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="mt-3 h-3 w-full" />
          <Skeleton className="mt-2 h-3 w-2/3" />
        </div>
      ))}
    </div>
  );
}

function EmptyState({ isAdmin, onCreate }: { isAdmin: boolean; onCreate?: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-md border border-dashed bg-card/40 p-12 text-center">
      <p className="text-sm text-muted-foreground">No announcements yet.</p>
      {isAdmin && onCreate && (
        <Button onClick={onCreate} variant="default" size="sm">
          <PlusIcon className="size-4" />
          Post the first one
        </Button>
      )}
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-md border border-destructive/40 bg-destructive/5 p-6 text-center">
      <p className="text-sm text-destructive">{message}</p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
